import { Autocomplete, MenuItem, Step, StepLabel, Stepper, TextField } from '@mui/material';
import { ISftpIntegrationTemplate, ISftpPartner } from '@core/models/sftp-transfer.model';
import { Supplier } from '@core/models/supplier.model';
import { IUser } from '@core/models/user-listing.model';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getAllUsersThunk } from '@redux/features/user-listing.slice';
import { RootState } from '@redux/store';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import { canPollPartner, canSendPartner } from './sftp-partner.utils';
import {
    connectionTestToastMessage,
    pollToastMessage,
    sendTestToastMessage,
    wizardErrorMessage,
} from './sftp-user-messages';
import SftpSetupGuide from './SftpSetupGuide';

type Direction = 'RECEIVE' | 'SEND' | 'BIDIRECTIONAL';
type AuthType = 'PASSWORD' | 'KEY' | 'PASSWORD_AND_KEY';

interface IProps {
    show: boolean;
    handleClose: () => void;
    onComplete: () => void;
}

function slugCode(name: string): string {
    return name
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
        .slice(0, 64);
}

function getUsersPageContent(raw: unknown): IUser[] {
    const u = raw as Record<string, unknown> | null | undefined;
    if (!u) return [];
    const inner = (u.usersWithPage as Record<string, unknown>) || u;
    return (inner.content as IUser[]) || [];
}

const SftpPartnerWizard: FC<IProps> = ({ show, handleClose, onComplete }) => {
    const dispatch = useAppDispatch();
    const { suppliers } = useAppSelector((state: RootState) => (state as RootState).supplierState || { suppliers: [] });
    const { users: usersRaw } = useAppSelector((state: RootState) => state.userListingState);

    const [step, setStep] = useState(0);
    const [templates, setTemplates] = useState<ISftpIntegrationTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<ISftpIntegrationTemplate | null>(null);
    const [direction, setDirection] = useState<Direction>('RECEIVE');
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [host, setHost] = useState('');
    const [port, setPort] = useState(22);
    const [username, setUsername] = useState('');
    const [authType, setAuthType] = useState<AuthType>('PASSWORD');
    const [password, setPassword] = useState('');
    const [privateKeyPem, setPrivateKeyPem] = useState('');
    const [fingerprint, setFingerprint] = useState('');
    const [remotePollPath, setRemotePollPath] = useState('');
    const [remoteUploadPath, setRemoteUploadPath] = useState('');
    const [remoteArchivePath, setRemoteArchivePath] = useState('');
    const [remoteQuarantinePath, setRemoteQuarantinePath] = useState('');
    const [filePatterns, setFilePatterns] = useState('["*.xml","*.json"]');
    const [pollCron, setPollCron] = useState('0 */5 * * * *');
    const [supplierId, setSupplierId] = useState<number | null>(null);
    const [pharmacyUserId, setPharmacyUserId] = useState<number | null>(null);
    const [epcisSchemaVersion, setEpcisSchemaVersion] = useState('1.2');
    const [epcisFormat, setEpcisFormat] = useState('XML');
    const [templateProfileCode, setTemplateProfileCode] = useState('');
    const [epcisTemplatePath, setEpcisTemplatePath] = useState(
        'classpath:integration/templates/lspedia-epcis-1.2-send-template.xml'
    );
    const [requiresAdminApproval, setRequiresAdminApproval] = useState(true);
    const [autoSendOnShip, setAutoSendOnShip] = useState(false);
    const [deliveryMode, setDeliveryMode] = useState<'REMOTE' | 'HOSTED'>('REMOTE');
    const [antarSftpAccountUsername, setAntarSftpAccountUsername] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [savedCode, setSavedCode] = useState<string | null>(null);
    const [testResult, setTestResult] = useState<string | null>(null);

    const pharmacyUsers = useMemo(
        () => getUsersPageContent(usersRaw).filter((u) => u.account?.accountType === 'PHARMACY'),
        [usersRaw]
    );

    const reset = () => {
        setStep(0);
        setSelectedTemplate(null);
        setDirection('RECEIVE');
        setCode('');
        setName('');
        setHost('');
        setPort(22);
        setUsername('');
        setAuthType('PASSWORD');
        setPassword('');
        setPrivateKeyPem('');
        setFingerprint('');
        setRemotePollPath('');
        setRemoteUploadPath('');
        setRemoteArchivePath('');
        setRemoteQuarantinePath('');
        setFilePatterns('["*.xml","*.json"]');
        setPollCron('0 */5 * * * *');
        setSupplierId(null);
        setPharmacyUserId(null);
        setErrors({});
        setSaving(false);
        setSavedCode(null);
        setTestResult(null);
        setEpcisTemplatePath('classpath:integration/templates/lspedia-epcis-1.2-send-template.xml');
        setRequiresAdminApproval(true);
        setAutoSendOnShip(false);
        setDeliveryMode('REMOTE');
        setAntarSftpAccountUsername('');
    };

    useEffect(() => {
        if (!show) {
            reset();
            return;
        }
        dispatch(getSuppliersThunk());
        dispatch(getAllUsersThunk({ pageNumber: '0', pageSize: '500', accountType: 'PHARMACY' }));
        sftpIntegrationService
            .listTemplates()
            .then((res) => setTemplates(res.data.templates || []))
            .catch(() => setTemplates([]));
    }, [show, dispatch]);

    const applyTemplate = (t: ISftpIntegrationTemplate) => {
        setSelectedTemplate(t);
        setDirection((t.direction as Direction) || 'RECEIVE');
        setTemplateProfileCode(t.code);
        const d = t.defaults || {};
        if (typeof d.host === 'string') setHost(d.host);
        if (typeof d.port === 'number') setPort(d.port);
        if (typeof d.remotePollPath === 'string') setRemotePollPath(d.remotePollPath);
        if (typeof d.remoteArchivePath === 'string') setRemoteArchivePath(d.remoteArchivePath);
        if (typeof d.remoteQuarantinePath === 'string') setRemoteQuarantinePath(d.remoteQuarantinePath);
        if (typeof d.filePatterns === 'string') setFilePatterns(d.filePatterns);
        if (typeof d.pollCron === 'string') setPollCron(d.pollCron);
        if (typeof d.remoteUploadPath === 'string') setRemoteUploadPath(d.remoteUploadPath);
        if (typeof d.epcisSchemaVersion === 'string') setEpcisSchemaVersion(d.epcisSchemaVersion);
        if (typeof d.epcisFormat === 'string') setEpcisFormat(d.epcisFormat);
        if (typeof d.epcisTemplatePath === 'string') setEpcisTemplatePath(d.epcisTemplatePath);
        if (typeof d.templateProfileCode === 'string') setTemplateProfileCode(d.templateProfileCode);
        if (typeof d.requiresAdminApproval === 'boolean') setRequiresAdminApproval(d.requiresAdminApproval);
        if (typeof d.authType === 'string') setAuthType(d.authType as AuthType);
    };

    const validateStep = (s: number): boolean => {
        const e: Record<string, string> = {};
        if (s === 0 && !selectedTemplate) e.template = 'Choose an integration type.';
        if (s === 1) {
            if (!name.trim()) e.name = 'Partner name is required.';
            if (!code.trim()) e.code = 'Partner code is required.';
            if (code.trim().toUpperCase() === 'LSPEDIA')
                e.code = 'This partner code is reserved for the server primary production SFTP partner. Choose another code.';
            if (!host.trim()) e.host = 'Host is required.';
            if (!port || port < 1 || port > 65535) e.port = 'Port must be between 1 and 65535.';
            if (!username.trim()) e.username = 'Username is required.';
            if ((authType === 'PASSWORD' || authType === 'PASSWORD_AND_KEY') && !password) {
                e.password = 'Password is required for this auth type.';
            }
            if ((authType === 'KEY' || authType === 'PASSWORD_AND_KEY') && !privateKeyPem.trim()) {
                e.privateKeyPem = 'Upload a private key for this auth type.';
            }
            if (privateKeyPem && !privateKeyPem.includes('BEGIN')) {
                e.privateKeyPem = "This file doesn't look like a private key. Upload a .pem or .key file.";
            }
        }
        if (s === 2) {
            if ((direction === 'RECEIVE' || direction === 'BIDIRECTIONAL') && !remotePollPath.trim()) {
                e.remotePollPath = 'Poll path is required for receive.';
            }
            if ((direction === 'SEND' || direction === 'BIDIRECTIONAL') && deliveryMode === 'REMOTE' && !remoteUploadPath.trim()) {
                e.remoteUploadPath = 'Upload path is required for remote send.';
            }
            if ((direction === 'SEND' || direction === 'BIDIRECTIONAL') && deliveryMode === 'HOSTED'
                    && !antarSftpAccountUsername.trim()) {
                e.antarSftpAccountUsername = 'Antar SFTP username is required for hosted delivery.';
            }
        }
        if (s === 3) {
            if ((direction === 'RECEIVE' || direction === 'BIDIRECTIONAL') && !supplierId) {
                e.supplierId = 'Supplier is required.';
            }
            if ((direction === 'SEND' || direction === 'BIDIRECTIONAL') && !pharmacyUserId) {
                e.pharmacyUserId = 'Pharmacy is required.';
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleDiscoverHostKey = async () => {
        if (!host.trim()) {
            setErrors((prev) => ({ ...prev, host: 'Enter host before discovering host key.' }));
            return;
        }
        try {
            const res = await sftpIntegrationService.discoverHostKey(code.trim().toUpperCase() || 'TEMP');
            setFingerprint(res.data.hostKeyDiscovery?.fingerprint || '');
        } catch (err) {
            setErrors((prev) => ({ ...prev, hostKey: wizardErrorMessage(err, 'Could not discover host key.') }));
        }
    };

    const handleSave = async () => {
        if (!validateStep(3)) return;
        setSaving(true);
        setTestResult(null);
        const partnerCode = code.trim().toUpperCase();
        const payload: Record<string, unknown> = {
            code: partnerCode,
            name: name.trim(),
            direction,
            host: host.trim(),
            port,
            username: username.trim(),
            authType,
            passwordEnvKey: '',
            privateKeyPathEnvKey: '',
            knownHostsPathEnvKey: '',
            remotePollPath: remotePollPath.trim() || null,
            remoteUploadPath: remoteUploadPath.trim() || null,
            remoteArchivePath: remoteArchivePath.trim() || null,
            remoteQuarantinePath: remoteQuarantinePath.trim() || null,
            filePatterns,
            pollCron,
            epcisSchemaVersion,
            epcisFormat,
            epcisTemplatePath: epcisTemplatePath.trim() || null,
            templateProfileCode: templateProfileCode || selectedTemplate?.code || null,
            requiresAdminApproval,
            autoSendOnShip,
            deliveryMode,
            antarSftpAccountUsername: antarSftpAccountUsername.trim() || null,
            environment: 'PRODUCTION',
            enabled: true,
            localStorageSubdir: partnerCode.toLowerCase(),
            supplierId: direction === 'SEND' ? null : supplierId,
            pharmacyUserId: direction === 'RECEIVE' ? null : pharmacyUserId,
        };
        try {
            await sftpIntegrationService.createIntegration(payload);
            await sftpIntegrationService.saveCredentials(partnerCode, {
                password: authType !== 'KEY' ? password : undefined,
                privateKeyPem: authType !== 'PASSWORD' ? privateKeyPem : undefined,
            });
            if (host.trim() && port) {
                try {
                    const scan = await sftpIntegrationService.discoverHostKey(partnerCode);
                    setFingerprint(scan.data.hostKeyDiscovery?.fingerprint || '');
                } catch {
                    /* optional after save */
                }
            }
            setSavedCode(partnerCode);
            setTestResult('Partner created and credentials saved.');
            onComplete();
        } catch (err) {
            setTestResult(wizardErrorMessage(err, 'Could not save partner.'));
        } finally {
            setSaving(false);
        }
    };

    const steps = ['Template', 'Connection', 'Paths', 'Links', 'Save & test'];

    return (
        <Modal
            show={show}
            onHide={() => {
                handleClose();
                reset();
            }}
            dialogClassName="mx-auto w-full max-w-[calc(100vw-1rem)] sm:max-w-[720px] p-4 shadow"
        >
            <Modal.Header className="shrink-0 border-b border-slate-200 pb-3">
                <Modal.Title className="text-lg font-semibold text-slate-800">Add SFTP integration</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4">
                <div className="mb-4">
                    <SftpSetupGuide />
                </div>
                <Stepper activeStep={step} alternativeLabel className="mb-6">
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {step === 0 && (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-slate-600">
                            Choose a template for a new trading partner. Use a unique partner code for each company you onboard for production.
                        </p>
                        {templates.map((t) => (
                            <button
                                key={t.code}
                                type="button"
                                className={`rounded border p-3 text-left ${selectedTemplate?.code === t.code ? 'border-[#bd902e] bg-[#bd902e10]' : 'border-slate-200'}`}
                                onClick={() => applyTemplate(t)}
                            >
                                <div className="font-semibold text-slate-800">{t.label}</div>
                                <div className="text-xs text-slate-500">{t.description}</div>
                            </button>
                        ))}
                        {errors.template && <p className="text-sm text-red-600">{errors.template}</p>}
                        <TextField
                            select
                            size="small"
                            label="Direction"
                            value={direction}
                            onChange={(e) => setDirection(e.target.value as Direction)}
                        >
                            <MenuItem value="RECEIVE">RECEIVE</MenuItem>
                            <MenuItem value="SEND">SEND</MenuItem>
                            <MenuItem value="BIDIRECTIONAL">BIDIRECTIONAL</MenuItem>
                        </TextField>
                    </div>
                )}

                {step === 1 && (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {(direction === 'SEND' || direction === 'BIDIRECTIONAL') && (
                            <div className="md:col-span-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                Antar connects to your partner&apos;s SFTP server to upload files. Host, port, and
                                credentials are the partner&apos;s server — not Antar&apos;s.
                            </div>
                        )}
                        {direction === 'RECEIVE' && (
                            <div className="md:col-span-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                                Antar connects to your partner&apos;s SFTP server to download files. Host, port, and
                                credentials are still required.
                            </div>
                        )}
                        <TextField size="small" label="Partner name *" value={name} onChange={(e) => {
                            setName(e.target.value);
                            if (!code) setCode(slugCode(e.target.value));
                        }} error={!!errors.name} helperText={errors.name} />
                        <TextField size="small" label="Partner code *" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} error={!!errors.code} helperText={errors.code} />
                        <TextField size="small" label="Host *" value={host} onChange={(e) => setHost(e.target.value)} error={!!errors.host} helperText={errors.host} />
                        <TextField size="small" label="Port *" type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} error={!!errors.port} helperText={errors.port} />
                        <TextField size="small" label="Username *" value={username} onChange={(e) => setUsername(e.target.value)} error={!!errors.username} helperText={errors.username} />
                        <TextField select size="small" label="Auth type" value={authType} onChange={(e) => setAuthType(e.target.value as AuthType)}>
                            <MenuItem value="PASSWORD">PASSWORD</MenuItem>
                            <MenuItem value="KEY">KEY</MenuItem>
                            <MenuItem value="PASSWORD_AND_KEY">PASSWORD_AND_KEY</MenuItem>
                        </TextField>
                        {(authType === 'PASSWORD' || authType === 'PASSWORD_AND_KEY') && (
                            <TextField size="small" type="password" label="SFTP password *" value={password} onChange={(e) => setPassword(e.target.value)} error={!!errors.password} helperText={errors.password || 'Stored encrypted — never shown again.'} />
                        )}
                        {(authType === 'KEY' || authType === 'PASSWORD_AND_KEY') && (
                            <div className="md:col-span-2">
                                <label className="mb-1 block text-sm font-medium text-slate-700">Private key (.pem) *</label>
                                <input type="file" accept=".pem,.key,.txt" onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;
                                    if (f.size > 8192) {
                                        setErrors((prev) => ({ ...prev, privateKeyPem: 'File is too large. Private key files are normally under 8 KB.' }));
                                        return;
                                    }
                                    const reader = new FileReader();
                                    reader.onload = () => setPrivateKeyPem(String(reader.result || ''));
                                    reader.readAsText(f);
                                }} />
                                {errors.privateKeyPem && <p className="text-sm text-red-600">{errors.privateKeyPem}</p>}
                            </div>
                        )}
                        {fingerprint && (
                            <div className="md:col-span-2 rounded bg-slate-100 px-3 py-2 text-xs text-slate-700">
                                Host key: {fingerprint}
                            </div>
                        )}
                        {errors.hostKey && <p className="text-sm text-red-600 md:col-span-2">{errors.hostKey}</p>}
                    </div>
                )}

                {step === 2 && (
                    <div className="grid grid-cols-1 gap-3">
                        {(direction === 'RECEIVE' || direction === 'BIDIRECTIONAL') && (
                            <TextField size="small" label="Remote poll path *" value={remotePollPath} onChange={(e) => setRemotePollPath(e.target.value)} error={!!errors.remotePollPath} helperText={errors.remotePollPath} />
                        )}
                        {(direction === 'SEND' || direction === 'BIDIRECTIONAL') && (
                            <>
                                <TextField
                                    select
                                    size="small"
                                    label="Outbound delivery mode"
                                    value={deliveryMode}
                                    onChange={(e) => setDeliveryMode(e.target.value as 'REMOTE' | 'HOSTED')}
                                >
                                    <MenuItem value="REMOTE">REMOTE — upload to partner SFTP</MenuItem>
                                    <MenuItem value="HOSTED">HOSTED — partner picks up from Antar</MenuItem>
                                </TextField>
                                {deliveryMode === 'REMOTE' && (
                                    <TextField size="small" label="Remote upload path *" value={remoteUploadPath} onChange={(e) => setRemoteUploadPath(e.target.value)} error={!!errors.remoteUploadPath} helperText={errors.remoteUploadPath} />
                                )}
                                {deliveryMode === 'HOSTED' && (
                                    <TextField size="small" label="Antar SFTP account username *" value={antarSftpAccountUsername} onChange={(e) => setAntarSftpAccountUsername(e.target.value)} error={!!errors.antarSftpAccountUsername} helperText={errors.antarSftpAccountUsername || 'Partner connects to Antar-hosted SFTP to download outbox files.'} />
                                )}
                            </>
                        )}
                        <TextField size="small" label="Archive path" value={remoteArchivePath} onChange={(e) => setRemoteArchivePath(e.target.value)} />
                        <TextField size="small" label="Quarantine path" value={remoteQuarantinePath} onChange={(e) => setRemoteQuarantinePath(e.target.value)} />
                        <TextField size="small" label="File patterns" value={filePatterns} onChange={(e) => setFilePatterns(e.target.value)} />
                        <TextField size="small" label="Poll cron" value={pollCron} onChange={(e) => setPollCron(e.target.value)} helperText="Default: every 5 minutes" />
                    </div>
                )}

                {step === 3 && (
                    <div className="flex flex-col gap-3">
                        {(direction === 'RECEIVE' || direction === 'BIDIRECTIONAL') && (
                            <Autocomplete
                                options={suppliers || []}
                                getOptionLabel={(o) => (o as Supplier).businessName || (o as Supplier).email || ''}
                                onChange={(_, v) => setSupplierId((v as Supplier)?.id ?? null)}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" label="Supplier *" error={!!errors.supplierId} helperText={errors.supplierId} />
                                )}
                            />
                        )}
                        {(direction === 'SEND' || direction === 'BIDIRECTIONAL') && (
                            <Autocomplete
                                options={pharmacyUsers}
                                getOptionLabel={(u) => u.account?.businessName ? `${u.account.businessName} (${u.email})` : u.email}
                                onChange={(_, v) => setPharmacyUserId(v?.id ?? null)}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" label="Pharmacy *" error={!!errors.pharmacyUserId} helperText={errors.pharmacyUserId} />
                                )}
                            />
                        )}
                        <TextField
                            size="small"
                            label="EPCIS schema version"
                            helperText="1.0, 1.2, or 2.0 — not limited to a single version"
                            value={epcisSchemaVersion}
                            onChange={(e) => setEpcisSchemaVersion(e.target.value)}
                        />
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={requiresAdminApproval} onChange={(e) => setRequiresAdminApproval(e.target.checked)} />
                            Requires admin approval
                        </label>
                        {(direction === 'SEND' || direction === 'BIDIRECTIONAL') && (
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={autoSendOnShip} onChange={(e) => setAutoSendOnShip(e.target.checked)} />
                                Auto-send EPCIS on ship
                            </label>
                        )}
                    </div>
                )}

                {step === 4 && (
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-slate-600">Save the partner and credentials, then test the connection.</p>
                        {testResult && <p className="rounded bg-slate-100 p-3 text-sm text-slate-800">{testResult}</p>}
                        {savedCode && (
                            <div className="flex flex-wrap gap-2">
                                <ButtonBase innerText="Test connection" onClick={async () => {
                                    const res = await sftpIntegrationService.testConnection(savedCode);
                                    setTestResult(connectionTestToastMessage(res.data.connectionTest?.message));
                                }} />
                                {canPollPartner(direction) && (
                                    <ButtonBase innerText="Download files" onClick={async () => {
                                        const res = await sftpIntegrationService.poll(savedCode);
                                        setTestResult(pollToastMessage(res.data?.message, savedCode));
                                    }} />
                                )}
                                {canSendPartner(direction) && (
                                    <ButtonBase innerText="Send test file" onClick={async () => {
                                        await sftpIntegrationService.sendTest(savedCode);
                                        setTestResult(sendTestToastMessage(null, savedCode));
                                    }} />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-3">
                <ButtonBase innerText="Cancel" onClick={() => { handleClose(); reset(); }} className="!bg-slate-200 !text-slate-800" />
                <div className="flex gap-2">
                    {step > 0 && (
                        <ButtonBase innerText="Back" onClick={() => setStep((s) => s - 1)} className="!bg-slate-200 !text-slate-800" />
                    )}
                    {step < 4 && (
                        <ButtonBase innerText="Next" onClick={() => { if (validateStep(step)) setStep((s) => s + 1); }} />
                    )}
                    {step === 4 && !savedCode && (
                        <ButtonBase innerText={saving ? 'Saving…' : 'Save'} onClick={handleSave} disabled={saving} />
                    )}
                    {savedCode && (
                        <ButtonBase innerText="Done" onClick={() => { handleClose(); reset(); }} />
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default SftpPartnerWizard;
